import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, useTheme} from "@mui/material";
import {Row} from "reactstrap";
import OperatorsAutoComplete from "../upsert-task-dialog/auto-complete";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import LazyImage from "../../app-specific/lazy-image";
import {ReactComponent as ImagePlaceholder} from "../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import {ReactComponent as CheckedBoxActive} from "../../../../assets/images/check-box/checkbox-active.svg";
import {ReactComponent as CheckedBox} from "../../../../assets/images/check-box/checkbox.svg";
import CacheService from "../../../../core/services/cache/cache_service";

const ChangePartSupportOperatorDialog = ({open, setOpen, operator, onSuccess, requestId}) => {
    const [assigning, setAssigning] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState(operator);
    const isMounted = useIsMounted()
    const theme = useTheme();

    const canSubmit = useMemo(() => selectedOperator && selectedOperator.id !== operator?.id, [selectedOperator, operator?.id]);

    /**
     * Assigns the selected operators to the list of assignees
     */
    useEffect(() => {
        if (open) {
            setSelectedOperator(operator);
            setAssigning(false)
        }
    }, [open]);

    /**
     * Assigns a new operator to the part support request.
     * @type {(function(): Promise<void>)}
     */
    const assignOperator = useCallback(async () => {
        setAssigning(true);
        const response = await Api.updatePartSupportOperator({
            id: requestId,
            newOperator: selectedOperator.id
        })
        if (!isMounted())
            return;
        setAssigning(false)
        if (response?.resultFlag) {
            onSuccess(requestId, selectedOperator);
            setOpen(false);
        }
    }, [isMounted, onSuccess, requestId, setOpen, selectedOperator]);


    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            loading={assigning}
            maxWidth={'sm'}
            className={'change-current-operator-password-dialog'}
        >
            <DialogTitle>
                <Row xs={12} className='no-gutters justify-content-center w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        Change Part Support Operator
                    </p>
                </Row>
            </DialogTitle>
            <DialogContent dividers className={''}>
                <div className={'assign-operator-popover d-flex flex-column align-items-center'}>
                    {
                        operator &&
                        <div className={'current-operator'} onClick={() => setSelectedOperator(operator)}>
                            <div className={'operator-details'}>
                                <div className={'option-image'}>
                                    <LazyImage
                                        alt={'profile'}
                                        noImage={<div style={{backgroundColor: `${theme.palette.background.default}`}}><ImagePlaceholder/></div>}
                                        src={CacheService.getOperatorProfileImage(operator?.id)}
                                        height={35}
                                    />
                                </div>
                                <div className={'current-operator-title'}>
                                    <p className={'ml-2'}>{`${operator?.firstName} ${operator?.lastName}`}</p>
                                </div>
                            </div>
                            {
                                selectedOperator?.id === operator.id
                                    ? <CheckedBoxActive/>
                                    : <CheckedBox/>
                            }
                        </div>
                    }
                    <OperatorsAutoComplete
                        multiple={false}
                        selectedItems={selectedOperator ? [selectedOperator] : []}
                        setSelectedItems={(operators) => setSelectedOperator(operators.length ? operators[0] : null)}
                        currentOperatorId={operator?.id}
                    />
                </div>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <div className={'d-flex w-100 justify-content-between'}>
                    <Button
                        disabled={!open || assigning}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={!open || assigning || !canSubmit}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={assignOperator}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            assigning
                                ? 'Assigning...'
                                : 'Assign'
                        }
                    </Button>
                </div>
            </DialogActions>
        </Dialog>

    );
}

export default ChangePartSupportOperatorDialog;
